import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { MEDIA_FOLDER_URL } from "@/lib/press";
import { TICKETS_URL, VENUE_URL, VENUE_NAME } from "@/lib/tickets";

const pressFeatures = [
  {
    publication: "Raw Vision",
    issue: "Issue 121",
    quote:
      "The international journal of intuitive, outsider and visionary art, featuring Nick Fraser among contemporary outsider artists shaping the field.",
    href: "https://rawvision.com",
    label: "Read the feature",
    accent: true,
  },
  {
    publication: "Strand Magazine",
    issue: "Sophie-May Ward-Marchbank",
    quote:
      "Featured alongside Raw Vision as Nick's work began to surface publicly through the estate's first releases.",
    href: "https://www.strandmagazine.co.uk/single-post/nick-fraser-at-the-crypt-gallery-resurrecting-east-london-s-van-gogh",
    label: "Read the feature",
  },
];

const pressAssets = [
  "Press release",
  "High-resolution artwork images",
  "Exhibition photography",
  "Short and long artist biography",
  "Estate and curator quotes",
  "Film stills",
  "Interview opportunities with Marcus O'Shaughnessy",
  "Information on Chapter 2 and the limited edition print release",
];

export default function Press() {
  return (
    <Layout>
      <main data-testid="press-page" className="bg-[color:var(--bone)]">
        {/* ── Press hero banner — instant context for journalists ── */}
        <section
          className="dark-section pt-40 md:pt-56 pb-20 md:pb-28"
          data-testid="press-hero-banner"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-8">For the Press</p>
              <h1
                className="font-wordmark text-[14vw] md:text-[9vw] lg:text-[8.5rem] leading-[0.92] tracking-wide"
                data-testid="press-hero-title"
              >
                Nick Fraser:<br />Chapter II
              </h1>
              <p
                className="font-serif italic text-2xl md:text-4xl lg:text-5xl leading-[1.2] mt-10 md:mt-14 max-w-[28ch] opacity-90"
                data-testid="press-hero-tagline"
              >
                Painted in private. Revealed in the East End.
              </p>
              <p
                className="hairline opacity-70 mt-10 md:mt-12"
                data-testid="press-hero-meta"
              >
                31 July 2026  ·{" "}
                <a
                  href={VENUE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet"
                  data-testid="press-hero-venue-link"
                >
                  {VENUE_NAME}
                </a>
              </p>

              <div className="mt-12 md:mt-16 flex flex-wrap items-center gap-4 md:gap-5">
                <Link
                  to="/press-release"
                  data-testid="press-hero-download"
                  className="hairline border border-[color:var(--bone)] px-6 py-3.5 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  Download Press Materials
                </Link>
                <a
                  href={TICKETS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="press-hero-tickets"
                  className="hairline border border-[color:var(--bone)] px-6 py-3.5 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  Book Tickets
                </a>
                <Link
                  to="/chapter-ii"
                  data-testid="press-hero-exhibition"
                  className="hairline border border-[color:var(--bone)] px-6 py-3.5 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  View Exhibition Information
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <PageHeader
          eyebrow="Press"
          title="A vision, slowly heard."
          lede="Selected press, features, film and writing on Nick Fraser's life, work and archive — gathered here for press, galleries, curators and those following the public release of his estate."
          image="/artworks/six-wise-guys.jpg"
        />

        {/* Editorial summary */}
        <section
          className="bone-section py-24 md:py-40"
          data-testid="press-summary"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal slow>
              <p className="hairline opacity-60 mb-10">Summary</p>
              <p className="font-serif italic text-2xl md:text-4xl leading-[1.25]">
                Nick Fraser was an East London artist who lived a modest
                working life while privately creating a vast and visionary body
                of work.
              </p>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.4] mt-8 opacity-80 max-w-3xl">
                After more than three decades at the Tate &amp; Lyle factory,
                Fraser left behind a powerful archive of paintings exploring
                faith, humour, masculinity, memory and the human body.
              </p>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.4] mt-8 opacity-80 max-w-3xl">
                Following the estate&apos;s first public exhibition, Chapter 1, his
                work is now being released in chapters. Chapter 2 marks a
                return to East London, bringing together original works,
                unseen archive footage, live performance and the first limited
                edition print release from the archive.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Film */}
        <section
          className="dark-section py-24 md:py-40"
          data-testid="press-film"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-5">The Short Film</p>
              <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05]">
                Watch the short film: The Legacy of Nick Fraser.
              </h2>
              <p className="font-serif italic text-lg md:text-xl opacity-80 mt-6 max-w-md leading-relaxed">
                An eleven-minute introduction to Nick Fraser&apos;s life, work and
                artistic journey, featuring archive material and insight into
                the East End artist whose private world is now being released
                in chapters.
              </p>
              <a
                href="https://www.youtube.com/watch?v=bjPlwtp4zzg"
                target="_blank"
                rel="noreferrer"
                className="link-quiet font-serif italic text-lg md:text-xl mt-10 inline-block"
                data-testid="press-film-link"
              >
                Watch on YouTube →
              </a>
            </Reveal>
            <Reveal className="md:col-span-7" slow>
              <div
                className="relative w-full overflow-hidden bg-black"
                style={{ aspectRatio: "16 / 9" }}
              >
                <iframe
                  src="https://www.youtube-nocookie.com/embed/bjPlwtp4zzg?rel=0&modestbranding=1"
                  title="The Legacy of Nick Fraser"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  data-testid="press-film-iframe"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Current press angle — Chapter 2 */}
        <section
          className="bone-section py-24 md:py-40"
          data-testid="press-chapter-2"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
            <Reveal className="md:col-span-7">
              <p className="hairline opacity-60 mb-5">Current press angle</p>
              <h2 className="font-serif italic text-4xl md:text-6xl leading-[1] max-w-[18ch]">
                Chapter 2: the East End return.
              </h2>
              <p className="font-serif italic text-xl md:text-2xl opacity-80 mt-10 leading-[1.4] max-w-[55ch]">
                Chapter 2 marks the next stage in the public release of Nick
                Fraser&apos;s archive, returning the work to East London through a
                one-day exhibition at Shoreditch Arts Club. The event brings
                together original works, unseen archive footage, a live
                performance from{" "}
                <a
                  href="https://www.instagram.com/bothaimusic"
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet"
                  data-testid="press-bothai-link"
                >
                  <em>BoThai</em>
                </a>{" "}
                and a limited edition print release created especially for
                this chapter.
              </p>
              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
                <Link
                  to="/press-release"
                  className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
                  data-testid="press-read-release-link"
                >
                  Read the press release →
                </Link>
                <Link
                  to="/media-kit"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-media-kit-link"
                >
                  Open media fact sheet →
                </Link>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-media-folder-link"
                >
                  Press image library →
                </a>
                <Link
                  to="/chapter-ii"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-chapter-2-link"
                >
                  View Chapter 2 →
                </Link>
                <Link
                  to="/contact?topic=press"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-request-materials-link"
                >
                  Request press materials →
                </Link>
              </div>
            </Reveal>
            <Reveal className="md:col-span-5" slow>
              <figure>
                <img
                  src="/posters/chapter-2.jpg"
                  alt="Chapter 2 — Shoreditch Arts Club, 31 July 2026"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </figure>
            </Reveal>
          </div>
        </section>

        {/* Selected press & features */}
        <section
          className="bone-section pb-24 md:pb-40"
          data-testid="press-selected"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">
                Selected press &amp; features
              </p>
            </Reveal>
            <div className="space-y-24 md:space-y-32 mt-12">
              {pressFeatures.map((p, i) => (
                <Reveal key={p.publication} slow>
                  <article
                    className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start"
                    data-testid={`press-${i}`}
                  >
                    <div className="md:col-span-4">
                      <p className="hairline opacity-60">{p.issue}</p>
                      <h3 className="font-serif italic text-3xl md:text-4xl leading-[1] mt-3">
                        {p.publication}
                      </h3>
                    </div>
                    <div className="md:col-span-8">
                      <p className="font-serif italic text-xl md:text-2xl leading-[1.4] opacity-90">
                        {p.quote}
                      </p>
                      {p.href && (
                        <a
                          href={p.href}
                          target="_blank"
                          rel="noreferrer"
                          className="link-quiet font-serif italic text-base md:text-lg mt-8 inline-block"
                          data-testid={`press-link-${i}`}
                        >
                          {p.label || "Read"} →
                        </a>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Quotes for press */}
        <section
          className="dark-section py-24 md:py-40"
          data-testid="press-quotes"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-10">Quotes for press</p>
            </Reveal>

            <div className="space-y-20 md:space-y-28">
              <Reveal slow>
                <blockquote>
                  <p className="font-serif italic text-2xl md:text-4xl leading-[1.25] max-w-[36ch]">
                    &ldquo;Chapter 2 is about bringing Nick&apos;s work back to the East
                    End, where his story began, and giving people the chance to
                    encounter the private world he created over a lifetime.&rdquo;
                  </p>
                  <footer className="mt-8">
                    <p className="hairline opacity-60">
                      Marcus O&apos;Shaughnessy · Curator
                    </p>
                  </footer>
                </blockquote>
              </Reveal>

              <Reveal slow>
                <blockquote className="md:text-right md:ml-auto md:max-w-[40ch]">
                  <p className="font-serif italic text-2xl md:text-4xl leading-[1.25]">
                    &ldquo;It has been an honour to protect Nick&apos;s work and help share
                    his story. He created with extraordinary honesty, humour
                    and spirit.&rdquo;
                  </p>
                  <footer className="mt-8">
                    <p className="hairline opacity-60">
                      Mark O&apos;Shaughnessy · Custodian of the Nick Fraser Estate
                    </p>
                  </footer>
                </blockquote>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Available assets */}
        <section
          className="bone-section py-24 md:py-40"
          data-testid="press-assets"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-5">Available on request</p>
              <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05]">
                Press materials.
              </h2>
              <p className="font-serif italic text-lg md:text-xl opacity-75 mt-6 max-w-md leading-relaxed">
                For journalists, curators, galleries and editors covering the
                Nick Fraser estate and Chapter 2.
              </p>
            </Reveal>

            <ul
              className="md:col-span-7 border-t border-[color:var(--rule)]"
              data-testid="press-assets-list"
            >
              {pressAssets.map((item, i) => (
                <Reveal key={item} delay={i * 50}>
                  <li className="border-b border-[color:var(--rule)] py-6 md:py-7 flex items-baseline gap-6">
                    <span className="hairline opacity-50 w-10">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif italic text-xl md:text-2xl">
                      {item}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Press contact */}
        <section
          className="dark-section py-32 md:py-48"
          data-testid="press-contact"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal slow>
              <p className="hairline opacity-60 mb-6">
                For press, galleries &amp; curators
              </p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.1] max-w-3xl">
                Press kits, high-resolution images, quotes and interview
                opportunities are available on request.
              </h2>
              <p className="font-serif italic text-xl opacity-80 mt-8 max-w-2xl leading-relaxed">
                For press enquiries, contact the estate directly or via the
                contact page.
              </p>
              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
                <a
                  href="mailto:info@nickfraserartist.com"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-email-link"
                >
                  info@nickfraserartist.com →
                </a>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-bottom-media-folder-link"
                >
                  Press image library →
                </a>
                <Link
                  to="/contact?topic=press"
                  className="link-quiet font-serif italic text-lg md:text-xl"
                  data-testid="press-contact-link"
                >
                  Contact page →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </Layout>
  );
}
